/**
 * ModerationAudit - who did what, and when
 * -----------------------------------------
 * The audit log has been written to since the beginning and, until now, could
 * only be read by opening the database by hand - which in practice means it
 * was never read at all.
 *
 * It is the answer to questions that arrive with no warning and want an answer
 * the same day:
 *
 *   "Why was my daughter taken off that job the night before?"
 *   "Who read my son's messages, and when?"
 *   "Has this poster cancelled on students before, or is this the first time?"
 *
 * A single complaint tells you about one incident. This is where a pattern
 * shows up.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ScrollText, Filter } from 'lucide-react';

import { safetyApi } from '../services/api';
import { dateTime } from '../utils/format';
import Loader from './Loader';
import EmptyState from './EmptyState';
import { useI18n } from '../hooks/useI18n';

/**
 * Plain English for each recorded action. The raw names are what the code
 * uses; nobody reading this at nine on a Sunday should have to translate
 * `application.cancelled_by_student` in their head.
 */
const LABELS = {
  'account.created': 'created an account',
  'account.deleted': 'closed an account',
  'account.suspended': 'suspended an account',
  'account.reinstated': 'reinstated an account',
  'password.reset': 'reset a password',
  'guardian.consent_given': 'a parent agreed',
  'guardian.consent_declined': 'a parent declined',
  'guardian.email_changed': 'changed a parent address',
  'chore.created': 'posted a chore',
  'chore.cancelled': 'cancelled a chore',
  'chore.completed': 'marked a chore complete',
  'chore.deleted': 'deleted a chore',
  'application.accepted': 'accepted a student',
  'application.rejected': 'turned a student down',
  'application.backup_set': 'named a backup',
  'application.cancelled_by_student': 'a student pulled out',
  'offer.published': 'published when they are free',
  'offer.withdrawn': 'withdrew their availability',
  'offer_request.created': 'asked a student about a time',
  'offer_request.accepted': 'a student agreed to a booking',
  'offer_request.declined': 'a student declined a booking',
  'report.created': 'reported somebody',
  'report.handled': 'handled a report',
  'user.blocked': 'blocked somebody',
  'user.unblocked': 'unblocked somebody',
  'messages.moderator_read': 'READ A PRIVATE CONVERSATION',
  'messages.hidden': 'hid a message',
  'messages.unhidden': 'put a message back',
};

/** The entries worth being able to isolate quickly. */
const QUICK_FILTERS = [
  { key: '', label: 'moderation.logEverything' },
  { key: 'messages.moderator_read', label: 'moderation.logConversationsRead' },
  { key: 'account.suspended', label: 'moderation.logSuspensions' },
  { key: 'report.created', label: 'moderation.logReports' },
  { key: 'chore.cancelled', label: 'moderation.logCancellations' },
  { key: 'guardian.consent_given', label: 'moderation.logConsentGiven' },
];

export default function ModerationAudit() {
  const { t } = useI18n();
  const [entries, setEntries] = useState([]);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safetyApi.auditLog({ action, limit: 200 });
      setEntries(data.entries || []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => { load(); }, [load]);

  const who = (person) =>
    person ? `${person.firstName} ${person.lastName || ''}`.trim() : t('moderation.theSiteItself');

  return (
    <>
      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
        {t('moderation.auditNote')}
      </p>

      <div className="row" style={{ gap: 6, marginBottom: 'var(--space-md)', flexWrap: 'wrap' }}>
        {QUICK_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`badge ${action === f.key ? 'badge-green' : 'badge-grey'}`}
            onClick={() => setAction(f.key)}
          >
            {f.key === '' && <Filter size={12} aria-hidden="true" />} {t(f.label)}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>{error}</div>}

      {loading ? (
        <Loader label={t('moderation.readingLog')} />
      ) : entries.length === 0 ? (
        <EmptyState icon={ScrollText} title={t('moderation.nothingRecorded')} message={t('moderation.noEntriesMatch')} />
      ) : (
        <div className="stack" style={{ gap: 0 }}>
          {entries.map((e) => (
            <div className="audit-line" key={e._id}>
              <div className="row-between" style={{ gap: 'var(--space-xs)' }}>
                <span className="text-sm">
                  <strong>{who(e.actor)}</strong>{' '}
                  <span className={e.action === 'messages.moderator_read' ? 'text-amber' : ''}>
                    {LABELS[e.action] || e.action}
                  </span>
                  {e.targetUser && (
                    <>
                      {' - '}
                      <Link to={`/${e.targetUser.role === 'student' ? 'students' : 'users'}/${e.targetUser._id}`}>
                        {who(e.targetUser)}
                      </Link>
                    </>
                  )}
                  {e.targetChore?.title && (
                    <span className="text-muted"> ({e.targetChore.title})</span>
                  )}
                </span>
                <span className="text-xs text-muted" style={{ whiteSpace: 'nowrap' }}>
                  {dateTime(e.createdAt)}
                </span>
              </div>

              {e.detail && Object.keys(e.detail).length > 0 && (
                <div className="text-xs text-muted" style={{ marginTop: 2 }}>
                  {Object.entries(e.detail)
                    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
                    .join(' · ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
